// The mind map's dialogs for the project on the map (design-docs/Maps — design.md ▸ Bringing the project in, Turn Into
// Scenes…): Add to Map… and Link… (a document from any arrangement but the Trash, or someone from the cast), Link Notes to
// Cast…, and Turn Into Scenes… (where, in what order, and the tree it makes).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MapScenes, type MapContent, type MapFrames, type MapScenesItem, type MapScenesOrder } from '@writing-suite/core';
import { Icon } from '../Icons.tsx';
import { EntityDot } from '../story/common.tsx';
import { matchesFind, type MapChoice, type SceneFolder } from './mapProject.ts';

function useModal() {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return ref;
}

function Dialog({ id, title, className, onCancel, onSubmit, children }: { id: string; title: string; className?: string; onCancel: () => void; onSubmit: () => void; children: ReactNode }) {
  const ref = useModal();
  return (
    <dialog
      ref={ref}
      className={`confirm text-dialog link-dialog map-dialog${className ? ` ${className}` : ''}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <h2 id={id}>{title}</h2>
        {children}
      </form>
    </dialog>
  );
}

/** Add to Map… or Link…: one document or one of the cast, found by name. */
export function MapPickerDialog({ title, confirm, documents, cast, onChoose, onCancel }: {
  title: string;
  confirm: string;
  documents: MapChoice[];
  cast: MapChoice[];
  onChoose: (choice: MapChoice) => void;
  onCancel: () => void;
}) {
  const [find, setFind] = useState('');
  const [chosen, setChosen] = useState<MapChoice | null>(null);
  const shownDocuments = useMemo(() => documents.filter((d) => matchesFind(d, find)).slice(0, 200), [documents, find]);
  const shownCast = useMemo(() => cast.filter((d) => matchesFind(d, find)).slice(0, 200), [cast, find]);
  const same = (a: MapChoice | null, b: MapChoice) => a !== null && a.kind === b.kind && a.id === b.id;
  const row = (c: MapChoice) => (
    <li
      key={`${c.kind}:${c.id}`}
      role="option"
      aria-selected={same(chosen, c)}
      data-map-choice={`${c.kind}:${c.id}`}
      className={same(chosen, c) ? 'selected' : undefined}
      tabIndex={0}
      onClick={() => setChosen(c)}
      onDoubleClick={() => onChoose(c)}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          e.preventDefault();
          setChosen(c);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          onChoose(c);
        }
      }}
    >
      {c.kind === 'entity' ? <EntityDot color={c.color} /> : null}
      <span className="link-document-text">
        <span className="link-document-title">{c.title}</span>
        <span className="link-document-place">{c.place}</span>
      </span>
      {same(chosen, c) ? <Icon name="check" size={14} className="link-document-check" /> : null}
    </li>
  );
  return (
    <Dialog id="map-picker-title" title={title} className="map-picker" onCancel={onCancel} onSubmit={() => chosen && onChoose(chosen)}>
      <input
        type="search"
        className="link-document-search"
        aria-label="Find"
        placeholder="Find a document or someone in the cast"
        value={find}
        autoFocus
        onChange={(e) => setFind(e.target.value)}
      />
      <div className="map-picker-lists">
        <h3 className="map-picker-heading" id="map-picker-documents">Documents</h3>
        <ul className="link-documents" role="listbox" aria-labelledby="map-picker-documents">
          {shownDocuments.map(row)}
          {shownDocuments.length === 0 ? <li className="link-documents-empty">No documents match.</li> : null}
        </ul>
        <h3 className="map-picker-heading" id="map-picker-cast">Cast</h3>
        <ul className="link-documents" role="listbox" aria-labelledby="map-picker-cast">
          {shownCast.map(row)}
          {shownCast.length === 0 ? <li className="link-documents-empty">{cast.length ? 'No one in the cast matches.' : 'No one in the cast yet.'}</li> : null}
        </ul>
      </div>
      <div className="actions">
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="button primary" disabled={!chosen}>
          {confirm}
        </button>
      </div>
    </Dialog>
  );
}

/** Link Notes to Cast…: each note that names someone, ticked, and Link. */
export function LinkCastDialog({ matches, onLink, onCancel }: {
  matches: { note: string; entity: string; words: string; name: string; color: string | null }[];
  onLink: (pairs: { note: string; entity: string }[]) => void;
  onCancel: () => void;
}) {
  const [off, setOff] = useState<Set<string>>(() => new Set());
  const chosen = matches.filter((m) => !off.has(m.note));
  return (
    <Dialog id="map-cast-title" title="Link Notes to Cast" className="map-cast" onCancel={onCancel} onSubmit={() => (matches.length ? onLink(chosen) : onCancel())}>
      {matches.length ? (
        <>
          <p className="link-dialog-note">These notes name someone in the cast. Linked, each shows them in their colour.</p>
          <ul className="map-cast-list">
            {matches.map((m) => (
              <li key={m.note} data-map-cast={m.note}>
                <label>
                  <input
                    type="checkbox"
                    checked={!off.has(m.note)}
                    onChange={(e) => setOff((o) => {
                      const next = new Set(o);
                      if (e.target.checked) next.delete(m.note);
                      else next.add(m.note);
                      return next;
                    })}
                  />
                  <span>
                    “{m.words}” → <EntityDot color={m.color} /> {m.name}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="confirm-message">No notes here name anyone in the cast.</p>
      )}
      <div className="actions">
        {matches.length ? (
          <>
            <button type="button" className="button secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={!chosen.length}>
              Link
            </button>
          </>
        ) : (
          <button type="submit" className="button primary" autoFocus>
            OK
          </button>
        )}
      </div>
    </Dialog>
  );
}

/** Where Turn Into Scenes… puts the documents. */
export type ScenesPlace = { kind: 'folder'; nodeId: string } | { kind: 'top' } | { kind: 'new'; name: string };

/** Turn Into Scenes…: the folder, the order, the tree it'll make, and Make. */
export function TurnIntoScenesDialog({ content, selected, frames, folders, mapTitle, noun, onMake, onCancel }: {
  content: MapContent;
  selected: ReadonlySet<string>;
  frames: MapFrames;
  folders: SceneFolder[];
  mapTitle: string;
  /** "scene", "post" or "document". */
  noun: string;
  onMake: (plan: MapScenesItem[], place: ScenesPlace) => void;
  onCancel: () => void;
}) {
  const [order, setOrder] = useState<MapScenesOrder>(() => MapScenes.defaultOrder(content, selected));
  const [where, setWhere] = useState<string>(folders.length ? `folder:${folders[0].nodeId}` : 'new');
  const [name, setName] = useState(mapTitle);
  const plan = useMemo(() => MapScenes.plan(content, selected, order, frames), [content, selected, order, frames]);
  const count = MapScenes.count(plan);
  const place = (): ScenesPlace => (where === 'top' ? { kind: 'top' } : where === 'new' ? { kind: 'new', name } : { kind: 'folder', nodeId: where.slice(7) });
  const tree = (items: MapScenesItem[]): ReactNode => (
    <ul>
      {items.map((i) => (
        <li key={i.noteID} data-scene-preview={i.title}>
          <span className={i.children.length ? 'map-scenes-folder' : undefined}>{i.title}</span>
          {i.children.length ? tree(i.children) : null}
        </li>
      ))}
    </ul>
  );
  const nouns = `${noun}${count === 1 ? '' : 's'}`;
  return (
    <Dialog id="map-scenes-title" title="Turn Into Scenes" className="map-scenes" onCancel={onCancel} onSubmit={() => count && onMake(plan, place())}>
      <label className="field-label stacked-label" htmlFor="map-scenes-where">
        Put them in
      </label>
      <select id="map-scenes-where" value={where} onChange={(e) => setWhere(e.target.value)}>
        {folders.map((f) => (
          <option key={f.nodeId} value={`folder:${f.nodeId}`}>
            {' '.repeat(f.depth * 3)}{f.title}
          </option>
        ))}
        <option value="top">The top level</option>
        <option value="new">A new folder</option>
      </select>
      {where === 'new' ? (
        <input className="map-scenes-name" aria-label="Folder name" value={name} placeholder="New Chapter" onChange={(e) => setName(e.target.value)} />
      ) : null}
      <fieldset className="map-scenes-order">
        <legend className="field-label">Order</legend>
        <label>
          <input type="radio" name="map-scenes-order" checked={order === 'lines'} onChange={() => setOrder('lines')} /> Along the lines
        </label>
        <label>
          <input type="radio" name="map-scenes-order" checked={order === 'page'} onChange={() => setOrder('page')} /> Across the page
        </label>
      </fieldset>
      <div className="map-scenes-preview" aria-label="What it makes">
        {tree(plan)}
      </div>
      <div className="actions">
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="button primary" disabled={!count}>
          {`Make ${count} ${nouns[0].toUpperCase()}${nouns.slice(1)}`}
        </button>
      </div>
    </Dialog>
  );
}
